import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useFirestoreEvents, FirestoreEvent } from '../hooks/useFirestoreEvents';

const Events: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [selectedEvent, setSelectedEvent] = useState<FirestoreEvent | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);

  // Load events data from Firestore
  const { events, loading, error } = useFirestoreEvents();

  // Pagination configuration
  const getEventsPerPage = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 9; // Large screens
      if (window.innerWidth >= 768) return 6; // Medium screens
      return 3; // Small screens
    }
    return 9;
  };

  const [eventsPerPage, setEventsPerPage] = React.useState(getEventsPerPage());

  React.useEffect(() => {
    const handleResize = () => {
      setEventsPerPage(getEventsPerPage());
      setCurrentPage(0); // Reset to first page on resize
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalPages = Math.ceil(events.length / eventsPerPage);
  const startIndex = currentPage * eventsPerPage;
  const endIndex = startIndex + eventsPerPage;
  const currentEvents = events.slice(startIndex, endIndex);
  
  // Scroll to events section when page changes
  const eventsRef = React.useRef<HTMLDivElement>(null);
  
  React.useEffect(() => {
    if (eventsRef.current && currentPage > 0) {
      eventsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage]);

  const openEventModal = (event: FirestoreEvent) => {
    setSelectedEvent(event);
    setSelectedImageIndex(0);
    setImageLoading(true);
  };

  const closeEventModal = () => {
    setSelectedEvent(null);
    setSelectedImageIndex(0);
    setImageLoading(false);
  };

  const nextImage = () => {
    if (selectedEvent && selectedEvent.images) {
      setImageLoading(true);
      setSelectedImageIndex((prev) => 
        prev === selectedEvent.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedEvent && selectedEvent.images) {
      setImageLoading(true);
      setSelectedImageIndex((prev) => 
        prev === 0 ? selectedEvent.images.length - 1 : prev - 1
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <section id="events" className="bg-gradient-to-br from-brand-light to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-purple mx-auto"></div>
            <p className="mt-4 text-brand-blue">Loading events...</p>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section id="events" className="bg-gradient-to-br from-brand-light to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-500">Error loading events: {error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 bg-brand-pink text-white px-6 py-2 rounded-bubble font-bold hover:bg-opacity-80 transition-all duration-300"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="events" ref={eventsRef} className="bg-gradient-to-br from-brand-light to-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-brand-purple mb-6">
            School <span className="text-brand-pink">Events</span>
          </h2>
          <p className="text-lg text-brand-blue max-w-2xl mx-auto">
            Relive the magical moments and exciting activities that make Neev special
          </p>
        </motion.div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {currentEvents.map((event, idx) => {
            const mainImage = event.images && event.images.length > 0 
              ? event.images[0].url 
              : 'https://via.placeholder.com/800x600/f0f0f0/cccccc?text=No+Image';
            const imageCount = event.images ? event.images.length : 0;
            
            return (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.6, delay: idx * 0.1, ease: 'easeOut' }}
                className="group relative cursor-pointer"
                onClick={() => openEventModal(event)}
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-bubble hover:shadow-brand transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-brand-yellow">
                  {/* Event Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={mainImage} 
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://via.placeholder.com/800x600/f0f0f0/cccccc?text=Image+Not+Available';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className={`inline-block px-3 py-1 text-xs font-bold text-${event.color} bg-white bg-opacity-90 rounded-full mb-2`}>
                        {event.category}
                      </span>
                      <h3 className="text-white font-bold text-lg">{event.title}</h3>
                    </div>
                  </div>

                  {/* Event Details */}
                  <div className="p-12">
                    <div className="flex items-center text-brand-blue mb-3">
                      <span className="text-brand-pink mr-2">📅</span>
                      <span className="font-semibold">{event.date}</span>
                    </div>
                    <p className="text-brand-dark leading-relaxed text-sm mb-4 line-clamp-3">
                      {event.description}
                    </p>
                    
                    {/* Event Highlights */}
                    <div className="space-y-1 mb-4">
                      {event.highlights.slice(0, 2).map((highlight, featureIdx) => (
                        <div key={featureIdx} className="flex items-center text-xs text-brand-dark">
                          <span className="text-brand-green mr-2">✓</span>
                          {highlight}
                        </div>
                      ))}
                    </div>

                    {/* View More Button */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-brand-blue font-medium">
                        {imageCount} photos
                      </span>
                      <motion.button
                        className={`bg-${event.color} text-white py-2 px-4 rounded-bubble font-bold text-sm hover:bg-opacity-80 transition-all duration-300`}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        View Event
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pagination Dots */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            className="flex justify-center items-center space-x-3 mb-12"
          >
            {/* Previous Button */}
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage((prev) => Math.max(0, prev - 1));
              }}
              disabled={currentPage === 0}
              className={`p-2 rounded-full ${
                currentPage === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-brand-pink hover:bg-brand-pink hover:text-white'
              } transition-all duration-300`}
              whileHover={currentPage !== 0 ? { scale: 1.1 } : {}}
              whileTap={currentPage !== 0 ? { scale: 0.9 } : {}}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </motion.button>

            {/* Dots */}
            {Array.from({ length: totalPages }).map((_, index) => (
              <motion.button
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentPage(index);
                }}
                className={`rounded-full transition-all duration-300 ${
                  index === currentPage
                    ? 'bg-brand-pink w-12 h-3'
                    : 'bg-brand-blue/30 hover:bg-brand-blue/50 w-3 h-3'
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}

            {/* Next Button */}
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
              }}
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-full ${
                currentPage === totalPages - 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-brand-pink hover:bg-brand-pink hover:text-white'
              } transition-all duration-300`}
              whileHover={currentPage !== totalPages - 1 ? { scale: 1.1 } : {}}
              whileTap={currentPage !== totalPages - 1 ? { scale: 0.9 } : {}}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </motion.button>
          </motion.div>
        )}

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
          className="text-center bg-gradient-to-r from-brand-pink to-brand-purple rounded-3xl p-8 text-white"
        >
          <h3 className="text-2xl font-bold mb-4">Join Our Next Event!</h3>
          <p className="text-lg mb-6 opacity-90">
            Be part of our upcoming activities and create lasting memories
          </p>
          <motion.button
            className="bg-brand-yellow text-brand-dark px-8 py-3 rounded-bubble font-bold hover:bg-opacity-90 transition-all duration-300"
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            Contact Us
          </motion.button>
        </motion.div>
      </div>

      {/* Event Modal */}
      <AnimatePresence>
        {selectedEvent && selectedEvent.images && (() => {
          const eventImages = selectedEvent.images;
          
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
              onClick={closeEventModal}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-white rounded-3xl max-w-4xl w-full max-h-[80vh] overflow-y-auto mx-16"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="p-12 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-brand-dark">{selectedEvent.title}</h3>
                      <p className="text-brand-blue">{selectedEvent.date}</p>
                    </div>
                    <motion.button
                      onClick={closeEventModal}
                      className="text-gray-500 hover:text-gray-700 text-2xl"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      ✕
                    </motion.button>
                  </div>
                </div>

                {/* Image Gallery */}
                <div className="relative overflow-hidden bg-gray-100">
                  {/* Loading Spinner */}
                  {imageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-brand-pink"></div>
                    </div>
                  )}
                  
                  <motion.div
                    key={selectedImageIndex}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.5}
                    onDragEnd={(e, { offset, velocity }) => {
                      const swipeThreshold = 50;
                      
                      if (offset.x > swipeThreshold) {
                        // Swiped right - go to previous image
                        prevImage();
                      } else if (offset.x < -swipeThreshold) {
                        // Swiped left - go to next image
                        nextImage();
                      }
                    }}
                    initial={{ x: 0, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="cursor-grab active:cursor-grabbing"
                  >
                    <img 
                      src={eventImages[selectedImageIndex].url} 
                      alt={selectedEvent.title}
                      className={`w-full h-64 md:h-96 object-contain select-none ${
                        imageLoading ? 'opacity-0' : 'opacity-100'
                      }`}
                      onLoadStart={() => setImageLoading(true)}
                      onLoad={() => setImageLoading(false)}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://via.placeholder.com/800x600/f0f0f0/cccccc?text=Image+Not+Available';
                        setImageLoading(false);
                      }}
                      draggable={false}
                    />
                  </motion.div>
                  
                  {/* Navigation Arrows */}
                    {eventImages.length > 1 && (
                      <>
                        <motion.button
                          onClick={prevImage}
                          className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 rounded-full p-2 md:p-3 text-brand-dark hover:bg-opacity-100 shadow-lg z-20 text-xl md:text-2xl"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          ←
                        </motion.button>
                        <motion.button
                          onClick={nextImage}
                          className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 rounded-full p-2 md:p-3 text-brand-dark hover:bg-opacity-100 shadow-lg z-20 text-xl md:text-2xl"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          →
                        </motion.button>
                      </>
                    )}

                  {/* Image Indicators */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
                    {eventImages.map((_, index) => (
                      <motion.button
                        key={index}
                        onClick={() => {
                          setImageLoading(true);
                          setSelectedImageIndex(index);
                        }}
                        className={`rounded-full transition-all duration-300 ${
                          index === selectedImageIndex 
                            ? 'bg-white w-3 h-3 md:w-4 md:h-4' 
                            : 'bg-white bg-opacity-50 w-2 h-2 md:w-3 md:h-3'
                        }`}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                      />
                    ))}
                  </div>
                  
                  {/* Swipe Hint - shown briefly on first image */}
                  {eventImages.length > 1 && selectedImageIndex === 0 && (
                    <motion.div
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 0 }}
                      transition={{ delay: 2, duration: 1 }}
                      className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-sm md:text-base z-20 pointer-events-none"
                    >
                      ← Swipe to browse →
                    </motion.div>
                  )}
                </div>

                {/* Event Details */}
                <div className="p-8">
                  <p className="text-brand-dark leading-relaxed mb-6">
                    {selectedEvent.description}
                  </p>
                  
                  <h4 className="font-bold text-brand-purple mb-3">Event Highlights:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedEvent.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-center text-sm text-brand-dark">
                        <span className="text-brand-green mr-2">✓</span>
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </section>
  );
};

export default Events; 